// src/contexts/AuthContext.js
import React, { createContext, useState, useEffect, useCallback } from "react";
import {
  loginUser,
  registerUser as registerUserService,
  logoutUser as logoutUserService,
  getStoredAuth,
  forgotPassword as forgotPasswordService,
  confirmEmail as confirmEmailService,
  resetPassword as resetPasswordService,
  isAuthenticated as isAuthenticatedService,
  resendVerification as resendVerificationService,
} from "../services/authService";
import { TOKEN_KEY, USER_KEY } from "../config";
const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true); // Start with loading true to check stored auth

  useEffect(() => {
    const { token: storedToken, user: storedUser } = getStoredAuth();
    isAuthenticatedService()
      .then((_) => {
        if (storedToken && storedUser) {
          setToken(storedToken);
          setUser(storedUser);
          setIsAuthenticated(true);
        }
      })
      .catch((error) => {
        console.error("Error checking authentication status:", error);
        setIsAuthenticated(false);
        setUser(null);
        setToken(null);
      })
      .finally(() => {
        setIsLoading(false); // Set loading to false after checking auth status
      });
  }, []);

  const login = useCallback(async (email, password) => {
    //setIsLoading(true);
    try {
      const userData = await loginUser(email, password);
      setUser(userData);
      setToken(userData.jwToken);
      setIsAuthenticated(true);
      localStorage.setItem(TOKEN_KEY, userData.jwToken);
      localStorage.setItem(USER_KEY, JSON.stringify(userData));
      return userData;
    } catch (error) {
      setIsAuthenticated(false);
      setUser(null);
      setToken(null);
      throw error;
    } finally {
      //setIsLoading(false);
    }
  }, []);

  const register = useCallback(async (userData) => {
    //setIsLoading(true);
    try {
      const response = await registerUserService(userData);
      // After registration, user usually needs to confirm email.
      // So, we don't log them in automatically here unless the API implies it.
      return response;
    } catch (error) {
      throw error;
    } finally {
      //setIsLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    logoutUserService();
    setUser(null);
    setToken(null);
    setIsAuthenticated(false);
  }, []);

  const forgotPassword = useCallback(async (email) => {
    //setIsLoading(true);
    try {
      return await forgotPasswordService(email);
    } catch (error) {
      throw error;
    } finally {
      //setIsLoading(false);
    }
  }, []);
  const resetPassword = useCallback(async (email, code, newPassword) => {
    //setIsLoading(true);
    try {
      const response = await resetPasswordService(email, code, newPassword);
      return response;
    } catch (error) {
      throw error;
    } finally {
      //setIsLoading(false);
    }
  }, []);
  const resendVerification = useCallback(async (email) => {
    //setIsLoading(true);
    try {
      const response = await resendVerificationService(email);
      return response;
    } catch (error) {
      throw error;
    } finally {
      //setIsLoading(false);
    }
  }, []);
  const confirmEmail = useCallback(async (userId, code) => {
    // setIsLoading(true);
    try {
      const response = await confirmEmailService(userId, code);
      // Optionally, if email confirmation also logs the user in or provides a token:
      // if (response.data && response.data.jwToken) {
      //   setUser(response.data);
      //   setToken(response.data.jwToken);
      //   setIsAuthenticated(true);
      //   localStorage.setItem(TOKEN_KEY, response.data.jwToken);
      //   localStorage.setItem(USER_KEY, JSON.stringify(response.data));
      // }
      return response;
    } catch (error) {
      throw error;
    } finally {
      //setIsLoading(false);
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAuthenticated,
        isLoading,
        login,
        register,
        logout,
        forgotPassword,
        confirmEmail,
        resetPassword,
        resendVerification,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
